<script setup lang="ts">
const props = defineProps<{
	type: 'other' | 'departments' | 'airports' | 'delivery-buses' | 'passenger-buses'
}>()

const hidden = ref(true)
const localePath = useLocalePath()

function show() {
	hidden.value = false
}

const { locale } = useI18n()
const { isMobile_lg } = useWindowWidth()
const airportsData = inject<Ref<ICMSLocationModel[] | undefined>>('airportsData') ?? ref<ICMSLocationModel[] | undefined>(undefined)
const branchesData = inject<Ref<ICMSLocationModel[] | undefined>>('branchesData') ?? ref<ICMSLocationModel[] | undefined>(undefined)
const otherLocationsData = inject<Ref<ICMSLocationModel[] | undefined>>('otherLocationsData') ?? ref<ICMSLocationModel[] | undefined>(undefined)
const deliveryBusesData = inject<Ref<ICMSLocationModel[] | undefined>>('deliveryBusesData') ?? ref<ICMSLocationModel[] | undefined>(undefined)
const passengerBusesData = inject<Ref<ICMSLocationModel[] | undefined>>('passengerBusesData') ?? ref<ICMSLocationModel[] | undefined>(undefined)

const getURLforLocation = (item: ICMSLocationModel & { translation_key_title: string }) => {
	const urlLocale = item[`isActive${locale.value.toUpperCase()}`] ? item[`url${locale.value.toUpperCase()}`] : undefined
	const url = item.urlPL

	if (!url && !urlLocale) return undefined
	else
		return urlLocale
			? localePath({ name: 'car-rental-urlSlug', params: { urlSlug: urlLocale } }, locale.value)
			: localePath({ name: 'car-rental-urlSlug', params: { urlSlug: url } }, 'pl')
}
</script>

<template>
	<section
		v-if="
			(props.type === 'departments' && branchesData && branchesData?.length > 0) ||
			(props.type === 'airports' && airportsData && airportsData?.length > 0) ||
			(props.type === 'other' && otherLocationsData && otherLocationsData.length > 0) ||
			(props.type === 'delivery-buses' && deliveryBusesData && deliveryBusesData.length > 0) ||
			(props.type === 'passenger-buses' && passengerBusesData && passengerBusesData.length > 0)
		"
		class="other">
		<h2 v-if="props.type === 'delivery-buses'" data-aos="fade-up" class="other__title">
			{{ $t('modules.locations_page_module.locations_page_section.delivery_buses') }}
		</h2>
		<h2 v-if="props.type === 'passenger-buses'" data-aos="fade-up" class="other__title">
			{{ $t('modules.locations_page_module.locations_page_section.passenger_buses') }}
		</h2>
		<h2 v-if="props.type === 'other'" data-aos="fade-up" class="other__title">
			{{ $t('modules.locations_page_module.locations_page_section.other_locations_in_poland') }}
		</h2>
		<h2 v-if="props.type === 'departments' && branchesData" data-aos="fade-up" class="other__title">
			{{ $t('modules.locations_page_module.locations_page_section.our_departments') }}
		</h2>
		<h2 v-if="props.type === 'airports' && airportsData" data-aos="fade-up" class="other__title">
			{{
				isMobile_lg
					? $t('modules.locations_page_module.locations_page_section.rent_and_pick_up_car')
					: $t('modules.locations_page_module.locations_page_section.rent_and_pick_up_car')
			}}
		</h2>
		<div data-aos="fade-up" class="other__container" :data-hidden="hidden">
			<template v-if="props.type === 'other' && otherLocationsData">
				<NuxtLink
					v-for="(item, index) in otherLocationsData"
					:key="index"
					class="other__item other__item--airports"
					:to="getURLforLocation(item)">
					<p class="other__item-title">{{ item[`name${locale.toUpperCase()}`] }}</p>
					<p v-if="item[`subtitle${locale.toUpperCase()}`]" class="other__item-subtitle">
						{{ item[`subtitle${locale.toUpperCase()}`] }}
					</p>
					<p v-if="item[`address${locale.toUpperCase()}`]" class="other__item-street">
						{{ item[`address${locale.toUpperCase()}`] }}
					</p>
					<ClientOnly>
						<a v-if="item.phoneNumber" :href="`tel:${item.phoneNumber?.replaceAll(' ', '')}`" class="other__item-phone" @click.stop>{{
							item.phoneNumber
						}}</a>
					</ClientOnly>
					<GlobalIcon class="other__item-icon other__item-icon--top" name="location-bold" :width="20" />
					<GlobalIcon v-if="getURLforLocation(item)" class="other__item-icon other__item-icon--bottom" name="arrow-right" :width="18" />
				</NuxtLink>
			</template>
			<template v-if="props.type === 'departments' && branchesData">
				<NuxtLink v-for="(item, index) in branchesData" :key="index" class="other__item other__item--airports" :to="getURLforLocation(item)">
					<p class="other__item-title">{{ item[`name${locale.toUpperCase()}`] }}</p>
					<p v-if="item[`subtitle${locale.toUpperCase()}`]" class="other__item-subtitle">
						{{ item[`subtitle${locale.toUpperCase()}`] }}
					</p>
					<p v-if="item[`address${locale.toUpperCase()}`]" class="other__item-street">
						{{ item[`address${locale.toUpperCase()}`] }}
					</p>
					<ClientOnly>
						<a v-if="item.phoneNumber" :href="`tel:${item.phoneNumber?.replaceAll(' ', '')}`" class="other__item-phone" @click.stop>{{
							item.phoneNumber
						}}</a>
					</ClientOnly>
					<GlobalIcon class="other__item-icon other__item-icon--top" name="building-4" :width="20" />
					<GlobalIcon v-if="getURLforLocation(item)" class="other__item-icon other__item-icon--bottom" name="arrow-right" :width="18" />
				</NuxtLink>
			</template>
			<template v-if="props.type === 'airports' && airportsData">
				<NuxtLink v-for="(item, index) in airportsData" :key="index" class="other__item other__item--airports" :to="getURLforLocation(item)">
					<p class="other__item-title">{{ item[`name${locale.toUpperCase()}`] }}</p>
					<p v-if="item[`subtitle${locale.toUpperCase()}`]" class="other__item-subtitle">
						{{ item[`subtitle${locale.toUpperCase()}`] }}
					</p>
					<p v-if="item[`address${locale.toUpperCase()}`]" class="other__item-street">
						{{ item[`address${locale.toUpperCase()}`] }}
					</p>
					<ClientOnly>
						<a v-if="item.phoneNumber" :href="`tel:${item.phoneNumber?.replaceAll(' ', '')}`" class="other__item-phone" @click.stop>{{
							item.phoneNumber
						}}</a>
					</ClientOnly>
					<GlobalIcon class="other__item-icon other__item-icon--top" name="airplane" :width="20" />
					<GlobalIcon v-if="getURLforLocation(item)" class="other__item-icon other__item-icon--bottom2" name="arrow-right" :width="18" />
				</NuxtLink>
			</template>
			<template v-if="props.type === 'delivery-buses' && deliveryBusesData">
				<NuxtLink
					v-for="(item, index) in deliveryBusesData"
					:key="index"
					class="other__item other__item--airports"
					:to="getURLforLocation(item)">
					<p class="other__item-title">{{ item[`name${locale.toUpperCase()}`] }}</p>
					<p v-if="item[`subtitle${locale.toUpperCase()}`]" class="other__item-subtitle">
						{{ item[`subtitle${locale.toUpperCase()}`] }}
					</p>
					<p v-if="item[`address${locale.toUpperCase()}`]" class="other__item-street">
						{{ item[`address${locale.toUpperCase()}`] }}
					</p>
					<ClientOnly>
						<a v-if="item.phoneNumber" :href="`tel:${item.phoneNumber?.replaceAll(' ', '')}`" class="other__item-phone" @click.stop>{{
							item.phoneNumber
						}}</a>
					</ClientOnly>
					<GlobalIcon class="other__item-icon other__item-icon--top" name="location-bold" :width="20" />
					<GlobalIcon v-if="getURLforLocation(item)" class="other__item-icon other__item-icon--bottom" name="arrow-right" :width="18" />
				</NuxtLink>
			</template>
			<template v-if="props.type === 'passenger-buses' && passengerBusesData">
				<NuxtLink
					v-for="(item, index) in passengerBusesData"
					:key="index"
					class="other__item other__item--airports"
					:to="getURLforLocation(item)">
					<p class="other__item-title">{{ item[`name${locale.toUpperCase()}`] }}</p>
					<p v-if="item[`subtitle${locale.toUpperCase()}`]" class="other__item-subtitle">
						{{ item[`subtitle${locale.toUpperCase()}`] }}
					</p>
					<p v-if="item[`address${locale.toUpperCase()}`]" class="other__item-street">
						{{ item[`address${locale.toUpperCase()}`] }}
					</p>
					<ClientOnly>
						<a v-if="item.phoneNumber" :href="`tel:${item.phoneNumber?.replaceAll(' ', '')}`" class="other__item-phone" @click.stop>{{
							item.phoneNumber
						}}</a>
					</ClientOnly>
					<GlobalIcon class="other__item-icon other__item-icon--top" name="location-bold" :width="20" />
					<GlobalIcon v-if="getURLforLocation(item)" class="other__item-icon other__item-icon--bottom" name="arrow-right" :width="18" />
				</NuxtLink>
			</template>
			<div class="other__gradient"></div>
			<ButtonPrimary class="other__show-btn" @click="show">{{
				$t('modules.locations_page_module.locations_page_section.read_more')
			}}</ButtonPrimary>
		</div>
	</section>
</template>

<style scoped lang="scss">
@import './LocationsPageSection.scss';
</style>
